import apiClient from "services/apiClient";
import { queryClient } from "services/queryClient";

import axios from "axios";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { SmileOutlined } from "@ant-design/icons";
import { Grid, notification } from "antd";
import Utils from "utils";
import { crossTabSync, MessageTypes } from "utils/crossTabSync";
import { logout } from "store/slices/authSlice";
import { ErrorExtractor } from "views/app-views/whatsapp/CustomUtils.js/utils";
import {
  useAppSettings,
  useBranding,
  useUserCredits,
  useSupportAgents,
} from "hooks/api";
import { useMedia } from "hooks/api";
const { useBreakpoint } = Grid;
const MyContext = createContext();

export const MyContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [expiredMediaIds, setExpiredMediaIds] = useState(() => {
    try {
      const stored = localStorage.getItem("expiredMediaIds");
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const [activeMediaIds, setActiveMediaIds] = useState(() => {
    try {
      const stored = localStorage.getItem("activeMediaIds");
      return stored ? new Map(JSON.parse(stored)) : new Map();
    } catch {
      return new Map();
    }
  });

  const isSameDay = useCallback((timestamp1, timestamp2) => {
    const date1 = new Date(timestamp1);
    const date2 = new Date(timestamp2);
    return date1.toDateString() === date2.toDateString();
  }, []);

  const [timestamp, setTimestamp] = useState(0);
  const [maintenance, setMaintenance] = useState(false);
  const [midnightTimestamp, setMidnightTimestamp] = useState(0);
  const [userBalance, setUserBalance] = useState("00.00");
  const [userAlertBalance, setUserAlertBalance] = useState(0);
  const [adminConfig, setAdminConfig] = useState([]);
  const [supportAgents, setSupportAgents] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [qualityData, setQualityData] = useState();
  const [brandingData, setBrandingData] = useState({});

  // Add this new state for balance check
  const [hasUtilityBalance, setHasUtilityBalance] = useState(false);
  const [hasMarketingBalance, setHasMarketingBalance] = useState(false);
  const [hasAuthBalance, setHasAuthBalance] = useState(false);

  const api = process.env.REACT_APP_API_PATH;
  const server = window.location.hostname;
  const isMobile = !Utils.getBreakPoint(useBreakpoint()).includes("lg");
  const isMainServer =
    server === "web.smsforyou.biz" ||
    server === "localhost" ||
    server === "192.168.0.146";
  const UserData = useSelector((state) => state?.auth?.user);
  const userConfig = UserData?.user_config;
  const authToken = useSelector((state) => state?.auth?.token);
  const themeData = useSelector((state) => state?.theme);
  const Permisson = useSelector((state) => state?.auth?.user?.permissions);
  const twoFactor = useSelector((state) => state?.auth?.twoFactor);
  const userRole = UserData?.role;

  const isSupportAgent = userRole === "Support Agent";
  const resolveLatestApiKey = (user) => {
    const keys = user?.api_key;
    if (!keys?.length) {
      return undefined;
    }

    return keys[keys.length - 1]?.key;
  };
  const apiKey = isSupportAgent
    ? resolveLatestApiKey(UserData?.reporting_user) ?? resolveLatestApiKey(UserData)
    : resolveLatestApiKey(UserData);
  const appId = UserData?.user_config?.app_id;
  const smsAlert = UserData?.text_alerts === "true";
  const whatsappAlert = UserData?.whatsapp_alerts === "true";
  const emailAlert = UserData?.email_alerts === "true";

  // Add utility function to check balance against message type price
  const checkUserBalance = useCallback(
    (messageType = "utility", messageCount = 1) => {
      if (!UserData?.pricing_model || !userBalance) return false;
      const pricePerMessage =
        UserData?.pricing_model?.[`${messageType}_price`] || 0;
      const requiredBalance = pricePerMessage * messageCount;
      //console.log('pricePerMessage, requiredBalance, userBalance', pricePerMessage, requiredBalance, userBalance);
      const result = parseFloat(userBalance) >= requiredBalance;

      return result;
    },
    [UserData, userBalance]
  );

  // Update balance check states whenever userBalance or UserData changes
  useEffect(() => {
    if (UserData?.pricing_model && userBalance) {
      setHasUtilityBalance(checkUserBalance("utility", 1));
      setHasMarketingBalance(checkUserBalance("marketing", 1));
      setHasAuthBalance(checkUserBalance("authentication", 1));
      //setHasServiceBalance(checkUserBalance('service', 1));
    } else {
      setHasUtilityBalance(false);
      setHasMarketingBalance(false);
      setHasAuthBalance(false);
      //setHasServiceBalance(false);
    }
  }, [UserData, userBalance, checkUserBalance]);

  const openNotification = useCallback((type) => {
    notification.open({
      message: "Alert Sent",
      description: `Alert has been sent to this user via ${type}.`,
      duration: 2,
      icon: <SmileOutlined style={{ color: "#108ee9" }} />,
    });
  }, []);

  // Enhanced function to check balance for multiple messages
  const canSendMessages = useCallback(
    (messageType, messageCount) => {
      return checkUserBalance(messageType, messageCount);
    },
    [checkUserBalance]
  );

  // Function to get insufficient balance message
  const getInsufficientBalanceMessage = useCallback(
    (messageType, messageCount = 1) => {
      if (!UserData?.pricing_model) return "Pricing information not available";

      const pricePerMessage =
        UserData?.pricing_model?.[`${messageType}_price`] || 0;
      const requiredBalance = pricePerMessage * messageCount;
      const shortfall = requiredBalance - parseFloat(userBalance);

      return `Insufficient balance. You need ₹${shortfall.toFixed(
        2
      )} more to send ${messageCount} ${messageType} message(s). Current balance: ₹${userBalance}`;
    },
    [UserData, userBalance]
  );

  const getMidnightTimestamp = useCallback(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.floor(now.getTime() / 1000);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // TanStack Query hooks — replace raw apiClient callbacks
  // ─────────────────────────────────────────────────────────────────────────────

  // Fetch global app settings
  const { data: settingsData } = useAppSettings({
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  // Fetch user credits/balance
  const { data: balanceFromQuery } = useUserCredits(UserData?.id, {
    enabled: !!UserData?.id,
  });

  // Fetch support agents
  const { data: supportAgentsData = [] } = useSupportAgents({ enabled: false });

  // Fetch user's media files
  const { data: mediaData = [] } = useMedia(UserData?.id, {
    enabled: !!UserData?.id,
  });

  // Fetch white-label branding (only for non-main servers)
  const { data: brandingDataFromQuery = {} } = useBranding(
    server,
    !isMainServer,
    {
      staleTime: 10 * 60 * 1000, // 10 min cache
    }
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Legacy API callbacks (now handled by TanStack Query hooks)
  // Kept for backward compatibility and exported for components that may still use them
  // ─────────────────────────────────────────────────────────────────────────────

  const UserCredits = useCallback(async () => {
    // Credits now come from useUserCredits hook
    // This callback is kept for compatibility but data syncs automatically below
  }, []);

  // Auto-fetch templates when user session is ready
  useEffect(() => {
    if (UserData && authToken) {
      setIsLoggedIn(true);
    }
  }, [UserData, authToken]);

  const GetNumberQuality = useCallback(async () => {
    if (!UserData?.id) {
      setQualityData();
      return;
    }

    try {
      const response = await apiClient.get("messaging/phone-numbers");
      setQualityData(response.data.data[0]);
    } catch (error) {
      setQualityData();
      console.error("Error fetching phone numbers:", error);
    }
  }, [UserData?.id]);

  useEffect(() => {
    const updateTimestamp = () => {
      setTimestamp(Math.floor(Date.now() / 1000));
      setMidnightTimestamp(getMidnightTimestamp());
    };
    updateTimestamp();
    const intervalId = setInterval(updateTimestamp, 1000);

    return () => clearInterval(intervalId);
  }, [getMidnightTimestamp]);

  useEffect(() => {
    if (!UserData?.id) return;

    // Set alert balance threshold
    setUserAlertBalance(UserData?.pricing_model?.price_alert);
  }, [UserData?.id, UserData?.pricing_model]);

  const encryptData = useCallback(
    (data) => {
      if (userRole === "Admin") {
        return data;
      } else {
        if (data?.length > 0) {
          if (data?.length <= 4) return data;
          const firstTwo = data?.substring(0, 2);
          const lastTwo = data?.substring(data?.length - 2);
          const middle = "*".repeat(data?.length - 4);
          return `${firstTwo}${middle}${lastTwo}`;
        }
      }
    },
    [userRole]
  );

  const DownloadExcelFile = useCallback((data, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName);
  }, []);

  const HandleExport = useCallback(
    async (path, fileName, id = null) => {
      try {
        const response = await apiClient.get(
          `${api}${path}/${id || UserData.id}`
        );
        const data = response.data?.data;
        DownloadExcelFile(data, fileName);
      } catch (error) {
        console.error("Error in HandleExport:", error);
      }
    },
    [api, UserData, DownloadExcelFile]
  );

  const HandleSendEmail = useCallback(
    async (id, body) => {
      try {
        const response = await apiClient.post(`${api}send-email/${id}`, body);
        if (response.data.status) {
          openNotification("Email");
        }
      } catch (error) {
        console.error("Error in HandleSendEmail:", error);
      }
    },
    [api, openNotification]
  );

  const modifyNumber = useCallback((number) => {
    let mob_number = String(number);
    if (mob_number.length === 10) {
      return "91" + mob_number;
    } else if (mob_number.length === 12) {
      return mob_number;
    }
  }, []);

  const handleWhatsappAlert = useCallback(
    async (id, apikey, number, name, price_alert, latest_balance) => {
      const modifiedNumber = modifyNumber(number);
      try {
        const response = await axios.post(
          `https://event.smsforyou.biz/api/send-messages?apikey=${apiKey}&to=${modifiedNumber}&type=T&tname=lowbalance1&values=${name},${price_alert},${latest_balance}`
        );
        if (response.data.status) {
          openNotification("Whatsapp");
        }
      } catch (error) {
        console.error("Error in handleWhatsappAlert:", error);
      }
    },
    [apiKey, modifyNumber, openNotification]
  );

  const HandleSendSMS = useCallback(
    async (number, message) => {
      const otpApi = `https://login.smsforyou.biz/V2/http-api.php?apikey=${adminConfig?.sms_apiKey}&senderid=${adminConfig?.sms_senderId}&number=${number}&message=${message}&format=json`;
      try {
        await axios.get(otpApi);
      } catch (error) {
        console.error("Error in HandleSendSMS:", error);
      }
    },
    [adminConfig?.sms_apiKey, adminConfig?.sms_senderId]
  );

  const handleMakeReport = useCallback(
    async (number, message_id) => {
      try {
        await apiClient.post(`${api}make-reports`, {
          message_id: message_id,
          waId: number,
          display_phone_number: UserData?.whatsapp_number,
        });
      } catch (error) {
        console.error("Error in handleMakeReport:", error);
      }
    },
    [api, UserData]
  );

  useEffect(() => {
    if (!UserData?.id) {
      return;
    }

    GetNumberQuality();
  }, [UserData?.id, GetNumberQuality]);

  const getMedia = useCallback(
    async (setState) => {
      // Media now comes from useMedia hook
      // Optionally sync to callback for backward compatibility
      if (mediaData && mediaData.length > 0) {
        setState(mediaData);
      }
      return mediaData || [];
    },
    [mediaData]
  );

  const getUrlFromMediaId = useCallback((mediaId, media) => {
    if (mediaId && media) {
      const mediaItem = media?.find((item) => item?.media_id === mediaId);
      // console.log('mediaItem', mediaItem);
      if (mediaItem) {
        return mediaItem?.path;
      } else {
        return "/img/others/dummy-header.jpg";
      }
    }
  }, []);

  // Replace the existing CheckExpiredMedia function
  const CheckExpiredMedia = useCallback(
    async (mediaId) => {
      // Check if media is in expired cache
      if (expiredMediaIds.has(mediaId)) {
        return false;
      }

      // Check if media is in active cache and was checked today
      const now = Date.now();
      if (activeMediaIds.has(mediaId)) {
        const lastChecked = activeMediaIds.get(mediaId);
        if (isSameDay(now, lastChecked)) {
          return true; // Media was active today, no need to check again
        }
      }
      try {
        const response = await apiClient.get(`messaging/media/${mediaId}`);

        if (response.status === 200) {
          // Update active media cache with current timestamp
          setActiveMediaIds((prev) => {
            const updated = new Map(prev).set(mediaId, now);
            localStorage.setItem(
              "activeMediaIds",
              JSON.stringify([...updated])
            );
            return updated;
          });
          return true;
        } else {
          // Add to expired media cache
          setExpiredMediaIds((prev) => {
            const updated = new Set(prev).add(mediaId);
            localStorage.setItem(
              "expiredMediaIds",
              JSON.stringify([...updated])
            );
            return updated;
          });
          return false;
        }
      } catch (error) {
        // Add to expired media cache on error
        setExpiredMediaIds((prev) => {
          const updated = new Set(prev).add(mediaId);
          localStorage.setItem("expiredMediaIds", JSON.stringify([...updated]));
          return updated;
        });
        return false;
      }
    },
    [expiredMediaIds, activeMediaIds, isSameDay]
  );

  // Add cleanup function for active media (run on app startup)
  useEffect(() => {
    const cleanupActiveMediaCache = () => {
      const now = Date.now();
      setActiveMediaIds((prev) => {
        const updated = new Map();
        prev.forEach((timestamp, mediaId) => {
          // Keep entries from today only
          if (isSameDay(now, timestamp)) {
            updated.set(mediaId, timestamp);
          }
        });
        localStorage.setItem("activeMediaIds", JSON.stringify([...updated]));
        return updated;
      });
    };

    // Clean up old active media entries on app start
    cleanupActiveMediaCache();
  }, [isSameDay]);

  const calculateRequredBalance = useCallback(
    (totalNUmbers, category) => {
      if (totalNUmbers && category) {
        let categoryType = [category] + "_price";
        let total = totalNUmbers * UserData?.pricing_model?.[categoryType];
        return total < userBalance;
      }
    },
    [UserData, userBalance]
  );

  //useMemo in buttonStyle
  const buttonStyle = useMemo(() => {
    return {
      background: "linear-gradient(116deg,  #40e495, #30dd8a, #2bb673)",
      color: "white",
      border: "none",
    };
  }, []);

  const getNumberCount = useCallback((numbers, number) => {
    if (number || numbers?.length > 0) {
      const count =
        numbers?.length +
        number
          ?.split(",")
          ?.map((item) => item.trim())
          ?.flatMap((item) => item.split("\n").map((subItem) => subItem.trim()))
          ?.filter(
            (item) => item !== "" && (item.length === 10 || item.length === 12)
          ).length;
      return count;
    } else {
      return 0;
    }
  }, []);

  // Sync hook data to context state
  useEffect(() => {
    // Sync settings
    if (settingsData) {
      setMaintenance(settingsData?.maintenance === 1);
      setAdminConfig(settingsData || {});
    }

    // Sync balance
    if (balanceFromQuery) {
      setUserBalance(balanceFromQuery);
    }

    // Sync support agents
    if (supportAgentsData?.length > 0) {
      setSupportAgents(supportAgentsData);
    }

    // Sync branding
    if (
      brandingDataFromQuery &&
      Object.keys(brandingDataFromQuery).length > 0
    ) {
      setBrandingData(brandingDataFromQuery);
    }
  }, [
    settingsData,
    balanceFromQuery,
    supportAgentsData,
    brandingDataFromQuery,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Remove FCM Token (must be defined before cross-tab sync)
  // ─────────────────────────────────────────────────────────────────────────────

  const removeFcmToken = useCallback(async () => {
    try {
      const fcm_device_id = localStorage.getItem("fcm_device_id");
      const response = await apiClient.post(`${api}remove-fcm-token`, {
        device_id: fcm_device_id,
        user_id: UserData?.id,
      });
      if (response.data.status) {
        localStorage.removeItem("fcm_device_id");
      }
    } catch (error) {
      return ErrorExtractor(error);
    }
  }, [api, UserData?.id]);

  // Cross-tab synchronization setup
  useEffect(() => {
    // Set up cross-tab message listeners
    const handleLogoutMessage = () => {
      removeFcmToken();
      dispatch(logout());
      queryClient.setQueryData(['messaging-templates'], []);
      localStorage.removeItem("AUTH_TOKEN");
      navigate("/login");
    };

    const handleLoginMessage = (loginData) => {
      // Force page reload to initialize with new auth state
      window.location.reload();
    };

    const handleBalanceUpdate = (newBalance) => {
      if (newBalance && newBalance !== userBalance) {
        setUserBalance(newBalance);
      }
    };

    const handleImpersonateStart = (data) => {
      // Force reload to update context with new user data
      window.location.reload();
    };

    const handleImpersonateEnd = () => {
      // Force reload to restore original user context
      window.location.reload();
    };

    const handleTemplateUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['messaging-templates'] });
    };

    const handleMaintenanceMode = (maintenanceStatus) => {
      setMaintenance(maintenanceStatus);
    };

    // Register cross-tab listeners
    crossTabSync.on(MessageTypes.USER_LOGOUT, handleLogoutMessage);
    crossTabSync.on(MessageTypes.USER_LOGIN, handleLoginMessage);
    crossTabSync.on(MessageTypes.BALANCE_UPDATE, handleBalanceUpdate);
    crossTabSync.on(MessageTypes.IMPERSONATE_START, handleImpersonateStart);
    crossTabSync.on(MessageTypes.IMPERSONATE_END, handleImpersonateEnd);
    crossTabSync.on(MessageTypes.TEMPLATE_UPDATE, handleTemplateUpdate);
    crossTabSync.on(MessageTypes.MAINTENANCE_MODE, handleMaintenanceMode);

    // Cleanup function
    return () => {
      crossTabSync.off(MessageTypes.USER_LOGOUT, handleLogoutMessage);
      crossTabSync.off(MessageTypes.USER_LOGIN, handleLoginMessage);
      crossTabSync.off(MessageTypes.BALANCE_UPDATE, handleBalanceUpdate);
      crossTabSync.off(MessageTypes.IMPERSONATE_START, handleImpersonateStart);
      crossTabSync.off(MessageTypes.IMPERSONATE_END, handleImpersonateEnd);
      crossTabSync.off(MessageTypes.TEMPLATE_UPDATE, handleTemplateUpdate);
      crossTabSync.off(MessageTypes.MAINTENANCE_MODE, handleMaintenanceMode);
    };
  }, [
    dispatch,
    navigate,
    userBalance,
    removeFcmToken,
  ]);

  // Function to broadcast balance updates
  const broadcastBalanceUpdate = useCallback((balance) => {
    crossTabSync.send(MessageTypes.BALANCE_UPDATE, balance);
  }, []);

  // Function to broadcast logout
  const broadcastLogout = useCallback(
    (reason = "manual") => {
      crossTabSync.send(MessageTypes.USER_LOGOUT, {
        reason,
        userId: UserData?.id,
      });
    },
    [UserData?.id]
  );

  // Function to broadcast login
  const broadcastLogin = useCallback((userData) => {
    crossTabSync.send(MessageTypes.USER_LOGIN, {
      userData,
      timestamp: Date.now(),
    });
  }, []);

  // Function to broadcast impersonation
  const broadcastImpersonation = useCallback((action, data = null) => {
    if (action === "start") {
      crossTabSync.send(MessageTypes.IMPERSONATE_START, data);
    } else if (action === "end") {
      crossTabSync.send(MessageTypes.IMPERSONATE_END, data);
    }
  }, []);

  // Function to broadcast template updates
  const broadcastTemplateUpdate = useCallback(() => {
    crossTabSync.send(MessageTypes.TEMPLATE_UPDATE);
  }, []);

  // Function to broadcast maintenance mode
  const broadcastMaintenanceMode = useCallback((status) => {
    crossTabSync.send(MessageTypes.MAINTENANCE_MODE, status);
  }, []);

  const contextValue = useMemo(() => {
    const contextDependencies = {
      api,
      authToken,
      UserData,
      themeData,
      navigate,
      userBalance,
      UserCredits,
      appId,
      Permisson,
      timestamp,
      twoFactor,
      userRole,
      smsAlert,
      whatsappAlert,
      emailAlert,
      adminConfig,
      encryptData,
      DownloadExcelFile,
      HandleExport,
      HandleSendEmail,
      handleWhatsappAlert,
      HandleSendSMS,
      handleMakeReport,
      maintenance,
      apiKey,
      userAlertBalance,
      setMaintenance,
      qualityData,
      calculateRequredBalance,
      modifyNumber,
      getMedia,
      getUrlFromMediaId,
      CheckExpiredMedia,
      buttonStyle,
      isSupportAgent,
      supportAgents,
      getNumberCount,
      isMobile,
      isMainServer,
      brandingData,
      setBrandingData,
      isLoggedIn,
      broadcastBalanceUpdate,
      broadcastLogout,
      broadcastLogin,
      broadcastImpersonation,
      broadcastTemplateUpdate,
      broadcastMaintenanceMode,
      hasUtilityBalance,
      hasMarketingBalance,
      hasAuthBalance,
      checkUserBalance,
      canSendMessages,
      getInsufficientBalanceMessage,
      removeFcmToken,
    };
    return { ...contextDependencies };
  }, [
    api,
    authToken,
    UserData,
    themeData,
    navigate,
    userBalance,
    UserCredits,
    appId,
    Permisson,
    timestamp,
    twoFactor,
    userRole,
    smsAlert,
    whatsappAlert,
    emailAlert,
    adminConfig,
    encryptData,
    DownloadExcelFile,
    HandleExport,
    HandleSendEmail,
    handleWhatsappAlert,
    HandleSendSMS,
    handleMakeReport,
    maintenance,
    apiKey,
    userAlertBalance,
    setMaintenance,
    qualityData,
    calculateRequredBalance,
    modifyNumber,
    getMedia,
    getUrlFromMediaId,
    CheckExpiredMedia,
    buttonStyle,
    isSupportAgent,
    supportAgents,
    getNumberCount,
    isMobile,
    isMainServer,
    brandingData,
    setBrandingData,
    isLoggedIn,
    broadcastBalanceUpdate,
    broadcastLogout,
    broadcastLogin,
    broadcastImpersonation,
    broadcastTemplateUpdate,
    broadcastMaintenanceMode,
    hasUtilityBalance,
    hasMarketingBalance,
    hasAuthBalance,
    checkUserBalance,
    canSendMessages,
    getInsufficientBalanceMessage,
    removeFcmToken,
  ]);

  return (
    <MyContext.Provider value={contextValue}>{children}</MyContext.Provider>
  );
};

export const useMyContext = () => useContext(MyContext);
